import type { SignUpFormValues } from '../schemas';
import type { SignInResponse } from './sign-up';

import { useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useMutation } from '@tanstack/react-query';
import { useSnapshot } from 'valtio/react';

import { ContractType } from '@paid-ui/enums/contract';
import { useToast } from '@paid-ui/ui/toast';
import { request } from '@paid-ui/utils/axios';

import { signUpManager } from '../models';
import { SignUpStep } from '../models/enums';
import { formatFormValues } from '../utils/format-form-values';
import { isIndividualSignUp } from '../utils/is-individual-sign-up';

type CompleteSignUpResponse = {
  data: {
    userId: string;
    email: string;
    groupId?: string;
  };
} | null;

export const useCompleteSignUp = () => {
  const toast = useToast();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { userGroupId } = useSnapshot(signUpManager);
  const contractId = searchParams.get('contractId');
  const contractType = searchParams.get('contractType')?.toUpperCase();
  const isSecondaryContract =
    !!contractType &&
    [ContractType.SUBCONTRACT, ContractType.SUPPLY_CONTRACT].includes(contractType);

  const runWhenSignInFailed = useCallback(() => {
    toast.success('You have successfully signed up');
    const newSearchParams = new URLSearchParams(globalThis.location.search);
    newSearchParams.delete('step');
    router.replace({
      pathname: '/signin',
      search: newSearchParams.toString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMutation({
    mutationKey: ['CompleteSignUp'],

    mutationFn: async (formValues: SignUpFormValues) => {
      const isExistingGroup = Boolean(userGroupId);

      const token = searchParams.get('token');
      const contractId = searchParams.get('contractId');
      const email = searchParams.get('email') ?? '';

      if (isExistingGroup) {
        if (contractId) {
          const { data } = await request.post<CompleteSignUpResponse>(
            `/users/complete-signup/existing-group`,
            {
              userInfo: formatFormValues(formValues, {
                withMainRole: true,
              }),
              token,
              email: email.trim(),
              contractId,
            },
          );

          return data?.data;
        } else {
          const { data } = await request.post<CompleteSignUpResponse>(
            `/users/complete-signup/group/existing`,
            {
              groupId: userGroupId,
              userInfo: formatFormValues(formValues),
              token,
            },
          );

          return data?.data;
        }
      } else {
        const userType =
          isSecondaryContract || !isIndividualSignUp(formValues) ? 'business' : 'individual';

        const { data } = await request.post<CompleteSignUpResponse>(
          `/users/complete-signup/${userType}`,
          formatFormValues(formValues),
          {
            params: {
              token,
              email: email.trim(),
              contractId,
            },
          },
        );

        return data?.data;
      }
    },
    async onSuccess(res, { email, password }) {
      try {
        const { data } = await request.post<SignInResponse>('/auth/signin', {
          account: email || res?.email,
          password,
        });

        if (!data?.access_token) {
          runWhenSignInFailed();
          return;
        }

        sessionStorage.setItem('access_token', data.access_token);

        if (contractId) {
          router.replace({
            pathname: '/signup',
            query: {
              ...Object.fromEntries(searchParams.entries()),
              step: userGroupId
                ? SignUpStep.SIGN_CONTRACT
                : isSecondaryContract
                  ? SignUpStep.REVIEW_CONTRACT
                  : SignUpStep.COMPLETE_CONTRACT,
            },
          });
        } else {
          router.replace('/');
          toast.success('You have successfully signed up');
        }
      } catch {
        runWhenSignInFailed();
      }
    },
    onError(err) {
      toast.error(err instanceof Error ? err.message : 'Failed to sign up');
    },
  });
};
